import {
    OptionQuoteViewModel,
    QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel,
    WarrantQuoteViewModel
} from "../../quote/models/QuoteViewModel";
import {PriceStrategy} from "../../components/inputs/price/input/PriceStrategy";
import {IsoDate, OffsetDateTime} from "../../pseudo-types/DateTypes";
import {
    OrderDestination,
    OrderLifetime,
    OrderNumber,
    OrderRecord,
    OrderSide,
    OrderState,
    OrderStatus,
    TmcLeg
} from "./OrderRecord";
import {Exchange} from "../../instruments/exchange/Exchange";
import {ExerciseStyle, OptionRecord} from "../../options/contracts/models/OptionChainRecord";
import {AccountBriefRecord} from "../../account/types/AccountBriefRecord";
import {PricingInstruction} from "./PricingInstruction";
import {OptionType} from "../../types/ChainService";

export type OrderViewModels = ReadonlyArray<OrderViewModel>;

export interface OrderViewModel<Q extends QuoteViewModel = QuoteViewModel> {
    readonly quote: Q | null;
    readonly priceStrategy: PriceStrategy;
    readonly account: AccountBriefRecord | null;
    readonly isAmendable: boolean;
    readonly hasQuote: boolean;
    readonly orderNumber: OrderNumber;
    readonly code: string;
    readonly destination: OrderDestination;
    readonly exchange: Exchange;
    readonly side: OrderSide;
    readonly lifetime: OrderLifetime;
    readonly quantity: number;
    readonly filledQuantity: number;
    readonly remainingQuantity: number;
    readonly pricingInstruction: PricingInstruction;
    readonly limit: number | null;
    readonly validUntil: OffsetDateTime | null;
    readonly isOpen: boolean;
    readonly isBuy: boolean;
    readonly isSell: boolean;
    readonly isCompleted: boolean;
    readonly isFilled: boolean;
    readonly isCancelled: boolean;
    readonly isFailed: boolean;
    readonly tradedPercent: number;
    readonly averagePrice: number;
    readonly theo: number | null;
    readonly type: OptionType | null;
    readonly delta: number | null;
    readonly vol: number | null;
    readonly strike: number | null;
    readonly expiry: IsoDate | null;
    readonly cvf: number;
    readonly created: OffsetDateTime;
    readonly updated: OffsetDateTime;
    readonly status: OrderStatus;
    readonly state: OrderState;
    readonly lastAction: string;
    readonly actionStatus: string;
    readonly stateDescription: string;
    readonly isMine: boolean;
    readonly isInternal: boolean;
    readonly parentOrderNumber: number;
    readonly uncommittedVolume: number;
    readonly parentOrderLimit: number;
}

export abstract class OptionOrderViewModel implements OrderViewModel<OptionQuoteViewModel> {

    protected constructor(protected readonly order: OrderRecord,
                          public readonly isAmendable: boolean,
                          public readonly quote: OptionQuoteViewModel | null,
                          public readonly priceStrategy: PriceStrategy,
                          public readonly account: AccountBriefRecord | null,
                          public readonly isMine: boolean,
                          public readonly isInternal: boolean,
                          protected readonly contract: OptionRecord | null) {
    }

    public readonly abstract averagePrice: number;

    public get parentOrderLimit(): number {
        return this.order.parentOrderLimit;
    }

    public get uncommittedVolume(): number {
        return this.order.uncommittedVolume;
    }

    public get orderNumber(): OrderNumber {
        return this.order.orderNumber;
    }
    public get parentOrderNumber(): number {
        return this.order.parentOrderNumber;
    }

    public get code(): string {
        return this.order.code;
    }

    public get exchange(): Exchange {
        return this.order.exchange;
    }

    public get destination(): OrderDestination {
        return this.order.destination;
    }

    public get side(): OrderSide {
        return this.order.side;
    }

    public get lifetime(): OrderLifetime {
        return this.order.lifetime;
    }

    public get quantity(): number {
        return this.order.quantity;
    }

    public get filledQuantity(): number {
        return this.order.doneQuantity;
    }

    public get remainingQuantity(): number {
        return this.order.remainingQuantity;
    }

    public get pricingInstruction(): PricingInstruction {
        return this.order.priceInstruction;
    }

    public get limit(): number | null {
        return this.order.price;
    }

    public get validUntil(): OffsetDateTime | null {
        return this.order.expiry;
    }

    public get isCancelled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity === 0);
    }

    public get isCompleted(): boolean {
        return this.order.state === OrderState.INACTIVE;
    }

    public get isFailed(): boolean {
        return false;
    }

    public get isFilled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity !== 0);
    }

    public get isOpen(): boolean {
        return this.order.state === OrderState.ACTIVE;
    }

    public get isBuy(): boolean {
        return this.order.side === OrderSide.BUY;
    }

    public get isSell(): boolean {
        return !this.isBuy;
    }

    public get tradedPercent(): number {
        const order = this.order;
        if (order.quantity === 0) {
            return 0;
        } else {
            return order.doneQuantity / order.quantity;
        }
    }

    public get created(): OffsetDateTime {
        return this.order.created;
    }

    public get updated(): OffsetDateTime {
        return this.order.updated;
    }

    public get status(): OrderStatus {
        return this.order.status;
    }

    public get state(): OrderState {
        return this.order.state;
    }

    public get lastAction(): string {
        return this.order.lastAction;
    }

    public get actionStatus(): string {
        return this.order.actionStatus;
    }

    public get stateDescription(): string {
        return this.order.stateDescription;
    }

    public get hasQuote(): boolean {
        return this.quote != null;
    }

    public get theo(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.theo;
        }
    }

    public get delta(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.delta;
        }
    }

    public get vol(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.vol;
        }
    }

    public get style(): ExerciseStyle | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.style;
        }
    }

    public get type(): OptionType | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.type;
        }
    }

    public get strike(): number | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.strike;
        }
    }

    public get expiry(): IsoDate | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.expiry;
        }
    }

    public get cvf(): number {
        if (this.contract == null) {
            return 100;
        } else {
            return this.contract.cvf;
        }
    }
}

export function isOptionOrderViewModel(model: OrderViewModel): model is OptionOrderViewModel {
    return model instanceof OptionOrderViewModel;
}

export abstract class TmcOrderViewModel implements OrderViewModel<TmcQuoteViewModel> {

    public readonly cvf: number;

    protected constructor(protected readonly order: OrderRecord,
                          public readonly isAmendable: boolean,
                          public readonly quote: TmcQuoteViewModel | null,
                          public readonly priceStrategy: PriceStrategy,
                          public readonly account: AccountBriefRecord | null,
                          public readonly isMine: boolean,
                          public readonly isInternal: boolean,
                          public readonly legs: ReadonlyArray<TmcOrderLegViewModel>) {
        this.cvf = legs.find(isOptionLegModel)?.cvf ?? 100;
    }

    public readonly abstract averagePrice: number;

    public get parentOrderLimit(): number {
        return this.order.parentOrderLimit;
    }

    public get uncommittedVolume(): number {
        return this.order.uncommittedVolume;
    }

    public get orderNumber(): OrderNumber {
        return this.order.orderNumber;
    }
    public get parentOrderNumber(): number {
        return this.order.parentOrderNumber;
    }
    public get code(): string {
        return this.order.code;
    }

    public get exchange(): Exchange {
        return this.order.exchange;
    }

    public get destination(): OrderDestination {
        return this.order.destination;
    }

    public get side(): OrderSide {
        return this.order.side;
    }

    public get lifetime(): OrderLifetime {
        return this.order.lifetime;
    }

    public get quantity(): number {
        return this.order.quantity;
    }

    public get filledQuantity(): number {
        return this.order.doneQuantity;
    }

    public get remainingQuantity(): number {
        return this.order.remainingQuantity;
    }

    public get pricingInstruction(): PricingInstruction {
        return this.order.priceInstruction;
    }

    public get limit(): number | null {
        return this.order.price;
    }

    public get validUntil(): OffsetDateTime | null {
        return this.order.expiry;
    }

    public get isCancelled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity === 0);
    }

    public get isCompleted(): boolean {
        return this.order.state === OrderState.INACTIVE;
    }

    public get isFailed(): boolean {
        return false;
    }

    public get isFilled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity !== 0);
    }

    public get isOpen(): boolean {
        return this.order.state === OrderState.ACTIVE;
    }

    public get isBuy(): boolean {
        return this.order.side === OrderSide.BUY;
    }

    public get isSell(): boolean {
        return !this.isBuy;
    }

    public get tradedPercent(): number {
        const order = this.order;
        if (order.quantity === 0) {
            return 0;
        } else {
            return order.doneQuantity / order.quantity;
        }
    }

    public get created(): OffsetDateTime {
        return this.order.created;
    }

    public get updated(): OffsetDateTime {
        return this.order.updated;
    }

    public get status(): OrderStatus {
        return this.order.status;
    }

    public get state(): OrderState {
        return this.order.state;
    }

    public get lastAction(): string {
        return this.order.lastAction;
    }

    public get actionStatus(): string {
        return this.order.actionStatus;
    }

    public get stateDescription(): string {
        return this.order.stateDescription;
    }

    public get hasQuote(): boolean {
        return this.quote != null;
    }

    public get theo(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.theo;
        }
    }

    public get type(): OptionType | null {
        return null;
    }

    public get strike(): number | null {
        return null;
    }

    public get expiry(): IsoDate | null {
        return null;
    }

    public get delta(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.delta;
        }
    }

    public get vol(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.vol;
        }
    }
}

export function isTmcOrderViewModel(model: OrderViewModel): model is TmcOrderViewModel {
    return model instanceof TmcOrderViewModel;
}

export abstract class TmcOrderLegViewModel implements TmcLeg {

    protected constructor(private readonly leg: TmcLeg,
                          public readonly quote: QuoteViewModel | null) {
    }

    public get code(): string {
        return this.leg.code;
    }

    public get exchange(): Exchange {
        return this.leg.exchange;
    }

    public get quantity(): number {
        return this.ratio;
    }

    public get side(): OrderSide {
        return this.leg.side;
    }

    public get ratio(): number {
        return this.leg.ratio;
    }
}

export class TmcOrderOptionLegViewModel extends TmcOrderLegViewModel {

    private constructor(leg: TmcLeg,
                        private readonly contract: OptionRecord | null,
                        quote: QuoteViewModel | null) {
        super(leg, quote);
    }

    public get style(): ExerciseStyle | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.style;
        }
    }

    public get type(): OptionType | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.type;
        }
    }

    public get strike(): number | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.strike;
        }
    }

    public get expiry(): IsoDate | null {
        if (this.contract == null) {
            return null;
        } else {
            return this.contract.expiry;
        }
    }

    public get cvf(): number {
        if (this.contract == null) {
            return 100;
        } else {
            return this.contract.cvf;
        }
    }

    public static of(leg: TmcLeg,
                     contract: OptionRecord | null,
                     quote: QuoteViewModel | null): TmcOrderOptionLegViewModel {
        return new TmcOrderOptionLegViewModel(leg, contract, quote);
    }
}

export function isOptionLegModel(model: TmcOrderLegViewModel): model is TmcOrderOptionLegViewModel {
    return model instanceof TmcOrderOptionLegViewModel;
}

export class TmcOrderEquityLegViewModel extends TmcOrderLegViewModel {

    private constructor(leg: TmcLeg,
                        quote: QuoteViewModel | null) {
        super(leg, quote);
    }

    public static of(leg: TmcLeg, quote: QuoteViewModel | null): TmcOrderEquityLegViewModel {
        return new TmcOrderEquityLegViewModel(leg, quote);
    }
}

export function isEquityLegModel(model: TmcOrderLegViewModel): model is TmcOrderEquityLegViewModel {
    return model instanceof TmcOrderEquityLegViewModel;
}

export type SingleOrderViewModel = OptionOrderViewModel | EquityOrderViewModel | WarrantOrderViewModel;

export abstract class EquityOrderViewModel implements OrderViewModel<UnderlyingQuoteViewModel> {

    protected constructor(protected readonly order: OrderRecord,
                          public readonly isAmendable: boolean,
                          public readonly quote: UnderlyingQuoteViewModel | null,
                          public readonly priceStrategy: PriceStrategy,
                          public readonly account: AccountBriefRecord | null,
                          public readonly isMine: boolean,
                          public readonly isInternal: boolean) {
    }

    public readonly abstract averagePrice: number;

    public get parentOrderLimit(): number {
        return this.order.parentOrderLimit;
    }

    public get uncommittedVolume(): number {
        return this.order.uncommittedVolume;
    }

    public get orderNumber(): OrderNumber {
        return this.order.orderNumber;
    }
    public get parentOrderNumber(): number {
        return this.order.parentOrderNumber;
    }

    public get code(): string {
        return this.order.code;
    }

    public get exchange(): Exchange {
        return this.order.exchange;
    }

    public get destination(): OrderDestination {
        return this.order.destination;
    }

    public get side(): OrderSide {
        return this.order.side;
    }

    public get lifetime(): OrderLifetime {
        return this.order.lifetime;
    }

    public get quantity(): number {
        return this.order.quantity;
    }

    public get filledQuantity(): number {
        return this.order.doneQuantity;
    }

    public get remainingQuantity(): number {
        return this.order.remainingQuantity;
    }

    public get pricingInstruction(): PricingInstruction {
        return this.order.priceInstruction;
    }

    public get limit(): number | null {
        return this.order.price;
    }

    public get validUntil(): OffsetDateTime | null {
        return this.order.expiry;
    }

    public get isCancelled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity === 0);
    }

    public get isCompleted(): boolean {
        return this.order.state === OrderState.INACTIVE;
    }

    public get isFailed(): boolean {
        return false;
    }

    public get isFilled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity !== 0);
    }

    public get isOpen(): boolean {
        return this.order.state === OrderState.ACTIVE;
    }

    public get isBuy(): boolean {
        return this.order.side === OrderSide.BUY;
    }

    public get isSell(): boolean {
        return !this.isBuy;
    }

    public get tradedPercent(): number {
        const order = this.order;
        if (order.quantity === 0) {
            return 0;
        } else {
            return order.doneQuantity / order.quantity;
        }
    }

    public get created(): OffsetDateTime {
        return this.order.created;
    }

    public get updated(): OffsetDateTime {
        return this.order.updated;
    }

    public get status(): OrderStatus {
        return this.order.status;
    }

    public get state(): OrderState {
        return this.order.state;
    }

    public get lastAction(): string {
        return this.order.lastAction;
    }

    public get actionStatus(): string {
        return this.order.actionStatus;
    }

    public get stateDescription(): string {
        return this.order.stateDescription;
    }

    public get hasQuote(): boolean {
        return this.quote != null;
    }

    public get theo(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.last;
        }
    }

    public get type(): OptionType | null {
        return null;
    }

    public get strike(): number | null {
        return null;
    }

    public get expiry(): IsoDate | null {
        return null;
    }

    public get delta(): number | null {
        return null;
    }

    public get vol(): number | null {
        return null;
    }

    public get cvf(): number {
        return 1;
    }
}

export function isEquityOrderViewModel(model: OrderViewModel): model is EquityOrderViewModel {
    return model instanceof EquityOrderViewModel;
}

export abstract class WarrantOrderViewModel implements OrderViewModel<WarrantQuoteViewModel> {

    protected constructor(protected readonly order: OrderRecord,
                          public readonly isAmendable: boolean,
                          public readonly quote: WarrantQuoteViewModel | null,
                          public readonly priceStrategy: PriceStrategy,
                          public readonly account: AccountBriefRecord | null,
                          public readonly isMine: boolean,
                          public readonly isInternal: boolean) {
    }

    public readonly abstract averagePrice: number;

    public get parentOrderLimit(): number {
        return this.order.parentOrderLimit;
    }

    public get uncommittedVolume(): number {
        return this.order.uncommittedVolume;
    }

    public get orderNumber(): OrderNumber {
        return this.order.orderNumber;
    }
    public get parentOrderNumber(): number {
        return this.order.parentOrderNumber;
    }

    public get code(): string {
        return this.order.code;
    }

    public get exchange(): Exchange {
        return this.order.exchange;
    }

    public get destination(): OrderDestination {
        return this.order.destination;
    }

    public get side(): OrderSide {
        return this.order.side;
    }

    public get lifetime(): OrderLifetime {
        return this.order.lifetime;
    }

    public get quantity(): number {
        return this.order.quantity;
    }

    public get filledQuantity(): number {
        return this.order.doneQuantity;
    }

    public get remainingQuantity(): number {
        return this.order.remainingQuantity;
    }

    public get pricingInstruction(): PricingInstruction {
        return this.order.priceInstruction;
    }

    public get limit(): number | null {
        return this.order.price;
    }

    public get validUntil(): OffsetDateTime | null {
        return this.order.expiry;
    }

    public get isCancelled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity === 0);
    }

    public get isCompleted(): boolean {
        return this.order.state === OrderState.INACTIVE;
    }

    public get isFailed(): boolean {
        return false;
    }

    public get isFilled(): boolean {
        return (this.order.state === OrderState.INACTIVE) && (this.order.doneQuantity !== 0);
    }

    public get isOpen(): boolean {
        return this.order.state === OrderState.ACTIVE;
    }

    public get isBuy(): boolean {
        return this.order.side === OrderSide.BUY;
    }

    public get isSell(): boolean {
        return !this.isBuy;
    }

    public get tradedPercent(): number {
        const order = this.order;
        if (order.quantity === 0) {
            return 0;
        } else {
            return order.doneQuantity / order.quantity;
        }
    }

    public get created(): OffsetDateTime {
        return this.order.created;
    }

    public get updated(): OffsetDateTime {
        return this.order.updated;
    }

    public get status(): OrderStatus {
        return this.order.status;
    }

    public get state(): OrderState {
        return this.order.state;
    }

    public get lastAction(): string {
        return this.order.lastAction;
    }

    public get actionStatus(): string {
        return this.order.actionStatus;
    }

    public get stateDescription(): string {
        return this.order.stateDescription;
    }

    public get hasQuote(): boolean {
        return this.quote != null;
    }

    public get theo(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.quote.last;
        }
    }

    public get type(): OptionType | null {
        return null;
    }

    public get strike(): number | null {
        return null;
    }

    public get expiry(): IsoDate | null {
        return null;
    }

    public get delta(): number | null {
        return null;
    }

    public get vol(): number | null {
        return null;
    }

    public get cvf(): number {
        return 1;
    }
}

export function isWarrantOrderViewModel(model: OrderViewModel): model is WarrantOrderViewModel {
    return model instanceof WarrantOrderViewModel;
}
